'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { usePathname } from '../i18n/navigation';
import { useCart } from './CartProvider';
import { urlLoginGoogle } from '../lib/store-api';

export default function AccountMenu() {
  const t = useTranslations();
  const pathname = usePathname();
  const { cliente, clienteLoading, logout } = useCart();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  async function handleLogout() {
    await logout();
    setOpen(false);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={t('nav.contaAria')}
        aria-haspopup="true"
        aria-expanded={open}
        className="p-2 hover:bg-gray-200 rounded transition-colors duration-150"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 bg-white shadow-lg z-50"
          style={{ border: '1px solid #ddd', borderRadius: '4px', minWidth: '240px', padding: '16px' }}
        >
          {clienteLoading ? null : cliente ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold truncate">{cliente.nome}</p>
                <p className="text-xs text-gray-500 truncate">{cliente.email}</p>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="text-sm text-left text-gray-600 hover:text-gray-900 underline"
              >
                {t('nav.contaSair')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="text-sm text-gray-600">{t('nav.contaHint')}</p>
              <a
                href={urlLoginGoogle(pathname)}
                className="flex items-center justify-center gap-2 border border-gray-300 rounded px-3 py-2 bg-white text-sm font-medium hover:bg-gray-50"
              >
                <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z" />
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.9 18.9 13 24 13c3.1 0 5.8 1.1 8 3l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
                  <path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5C29.6 34.9 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.3-4.1 5.8l6.5 5.5C41.5 36 44 30.4 44 24c0-1.3-.1-2.7-.4-3.5z" />
                </svg>
                {t('nav.contaEntrarGoogle')}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
